import {
  BuildingOfficeIcon,
  Chart,
  FireIcon,
  Flex,
  Grid,
  PanelProps,
  RectangleStackIcon,
  Text,
  UsersIcon,
} from 'components'
import { useWorkspace } from 'components/providers'
import dayjs from 'dayjs'
import { startCase } from 'lodash-es'
import { useEffect, useRef } from 'react'
import { AggregationData, LeadTemperature, leadTemperatures } from 'types'
import { StatTile } from '..'
import { useAggregations } from '../../utils/analyticsUtils'
import LeadTempIcon from '../event/LeadTempIcon'
import { useWorkspaceAnalytics } from './WorkspaceAnalyticsProvider'

export default function WorkspaceAnalyticsOverviewSection() {
  const { workspace } = useWorkspace()
  const { setStatus } = useWorkspaceAnalytics()
  const {
    apps,
    companyCount,
    followUps,
    leadCount,
    optIns,
    schoolCount,
    sessionsByMonth,
    sessionCount,
    temps,
  } = useOverviewAnalytics()

  const hasData = useRef(false)
  useEffect(() => {
    hasData.current = false
  }, [workspace?.id])

  useEffect(() => {
    if (sessionCount != null) {
      if (sessionCount > 0) hasData.current = true
      setStatus(sessionCount ? 'loaded' : hasData.current ? 'filtered-empty' : 'empty')
    }
  }, [sessionCount, setStatus])

  const tempCount = temps.data
    ?.filter(r => r.temp)
    .reduce((total, r) => total + ((r._count as number) || 0), 0)

  return (
    <Flex direction="column">
      <Grid templateColumns={`repeat(auto-fill, minmax(270px, 1fr))`} gap={6}>
        <StatTile label="Total Sessions" num={sessionCount} Icon={UsersIcon} />
        <StatTile label="Total Leads" num={leadCount} Icon={UsersIcon} />
        <StatTile label="Total Opt Ins" num={optIns} Icon={UsersIcon} />
        <StatTile label="Total Follow Ups" num={followUps} Icon={UsersIcon} />
        {!!companyCount && (
          <StatTile label="Companies" num={companyCount} Icon={BuildingOfficeIcon} />
        )}
        {!!schoolCount && <StatTile label="Schools" num={schoolCount} Icon={BuildingOfficeIcon} />}
      </Grid>

      <Flex gap={6} mt={6} flexWrap="wrap">
        {tempCount > 0 && (
          <StatTile
            {...flexOverviewItemProps}
            flexBasis="500px"
            label="Selected Temperatures"
            num={tempCount}
            Icon={FireIcon}
            canCopy
          >
            <Flex flex={1} overflow="hidden" gap={5} px={5}>
              <Chart legend={{ customLegend: true }} {...temps} />
              <Flex direction="column" gap={1}>
                {temps.data.map(({ temp, _count }) => (
                  <Flex key={temp} direction="column">
                    <Flex gap={2} align="center">
                      <LeadTempIcon value={temp as LeadTemperature} />
                      <Text textStyle="bodySmall">{startCase((temp as string) || 'unset')}</Text>
                    </Flex>
                    <Text textStyle="h2" textAlign="center">
                      {_count}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            </Flex>
          </StatTile>
        )}

        <StatTile
          {...flexOverviewItemProps}
          flexBasis="400px"
          label="Apps Used"
          Icon={RectangleStackIcon}
          canCopy
        >
          <Chart {...apps} />
        </StatTile>

        <StatTile
          {...flexOverviewItemProps}
          flexBasis="400px"
          label="Sessions Per Month"
          Icon={RectangleStackIcon}
          canCopy
        >
          <Chart
            {...sessionsByMonth}
            type="combo"
            categoryInfo={[{ formatter: v => dayjs(v).format("MMM 'YY") }]}
          />
        </StatTile>
      </Flex>
    </Flex>
  )
}

const flexOverviewItemProps: PanelProps = { alignItems: 'stretch', flexGrow: 1, height: '365px' }

function useOverviewAnalytics() {
  const { dateRange } = useWorkspaceAnalytics()
  const { aggregations } = useAggregations(
    [
      {
        table: 'leads',
        values: ['_count'],
      },
      {
        table: 'leadSessions',
        values: ['_count'],
      },
      {
        filters: [{ field: 'company', operator: 'isNull', value: false, table: 'leads' }],
        table: 'leads',
        values: ['_count'],
      },
      {
        table: 'leads',
        filters: [{ field: 'school', operator: 'isNull', value: false, table: 'leads' }],
        values: ['_count'],
      },
      {
        table: 'leads',
        categories: ['temp'],
        values: ['_count'],
        filters: [{ field: 'temp', operator: 'isNull', value: false, table: 'leads' }],
      },
      {
        table: 'leadSessions',
        categories: ['appName'],
        values: ['_count'],
      },
      {
        table: 'leads',
        values: ['_count'],
        categories: ['optIn'],
        filters: [{ field: 'optIn', operator: 'eq', value: true, table: 'leads' }],
      },
      {
        table: 'leads',
        values: ['_count'],
        categories: ['followUp'],
        filters: [{ field: 'followUp', operator: 'eq', value: true, table: 'leads' }],
      },
      {
        table: 'leadSessions',
        values: ['_count'],
        categories: ['createdMonth'],
        sort: { createdMonth: 'ASC' },
      },
    ],
    { dateRange }
  )

  const [
    leadCount,
    sessionCount,
    companyCount,
    schoolCount,
    temps,
    apps,
    optIns,
    followUps,
    sessionsByMonth,
  ] = aggregations || {}

  const sortedTemps = [...leadTemperatures, null]
    .map(label => temps?.data?.find(r => r.temp === label))
    .filter(Boolean) as AggregationData

  return {
    apps,
    companyCount: companyCount?.data?.[0]?.company_count as number,
    followUps: followUps?.data?.[0]?._count as number,
    leadCount: leadCount?.data?.[0]?._count as number,
    optIns: optIns?.data?.[0]?._count as number,
    schoolCount: schoolCount?.data?.[0]?.school_count as number,
    sessionsByMonth,
    sessionCount: sessionCount?.data?.[0]?._count as number,
    temps: { ...temps, data: sortedTemps },
  }
}
